import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

import { useBranding } from '@/utils/api';
import { Form } from '@/utils/api/aboboApiTypes';
import { WizardPayload } from '../SharedTypes';
import { FormWizardProvider, useFormWizard } from '@/contexts/FormWizardContext';
import { questionTypeRegistry } from '../QuestionTypeRegistry';

// Import shadcn components
import { HStack, Button, Separator } from '@/components/ui';

// Import question types
import MultipleChoice from './QuestionTypes/MultipleChoice';
import Informational from './QuestionTypes/Informational';
import OpenEnded from './QuestionTypes/OpenEnded';
import DatePicker from './QuestionTypes/DatePicker';
import Numeric from './QuestionTypes/Numeric';
import Phone from './QuestionTypes/Phone';
import FileUpload from './QuestionTypes/FileUpload';
import Address from './QuestionTypes/Address';


// Register question types at module level
questionTypeRegistry.register('multiple_choice', {
  component: MultipleChoice,
  name: 'Multiple Choice',
});

questionTypeRegistry.register('informational', {
  component: Informational,
  name: 'Informational',
});

questionTypeRegistry.register('open_ended_long', {
  component: OpenEnded,
  name: 'Open Ended Long',
});

questionTypeRegistry.register('open_ended_short', {
  component: OpenEnded,
  name: 'Open Ended Short',
});

questionTypeRegistry.register('date', {
  component: DatePicker,
  name: 'Date Picker',
});

questionTypeRegistry.register('numeric', {
  component: Numeric,
  name: 'Numeric',
});

questionTypeRegistry.register('phone', {
  component: Phone,
  name: 'Phone',
});

questionTypeRegistry.register('upload', {
  component: FileUpload,
  name: 'File Upload',
});

questionTypeRegistry.register('address', {
  component: Address,
  name: 'Address',
});

interface WizardProps {
  form: Form;
  onSave?: (payload: WizardPayload) => Promise<any> | any;
  onComplete: (payload: WizardPayload) => Promise<any> | any;
  onBack?: () => void;
  responseId?: string;
  dunBtnTxt?: string;
  onFirstQuestionChange?: (isFirst: boolean) => void;
  showClickWrap?: boolean;
  onClickWrapShown?: () => void;
}

// Internal wizard component that handles UI rendering and effects
function WizardContent({ dunBtnTxt, onFirstQuestionChange, showClickWrap, onClickWrapShown }: { dunBtnTxt?: string; onFirstQuestionChange?: (isFirst: boolean) => void; showClickWrap?: boolean; onClickWrapShown?: () => void }) {
  const { t } = useTranslation();
  const { branding, client } = useBranding();
  const questionRef = useRef<HTMLDivElement>(null);
  
  const {
    currentNode,
    visitedNodeIds,
    canGoBack,
    isLastStep,
    handleNext,
    goToPreviousNode,
    responseState,
  } = useFormWizard();

  const forwardButtonText = () => {
    if (isLastStep) {
      if (currentNode?.metadata?.exitPoint) {
        return t('form.buttons.exit');
      }
      return dunBtnTxt || t('form.buttons.submit');
    }
    return t('form.buttons.next');
  };

  // Handle focus and scroll
  useEffect(() => {
    if (questionRef.current) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      questionRef.current.focus({ preventScroll: true });
    }
  }, [currentNode]);

  // Notify parent when first question status changes
  useEffect(() => {
    onFirstQuestionChange?.(visitedNodeIds.length === 1);
  }, [visitedNodeIds.length, onFirstQuestionChange]);

  // Wrap handleNext to notify parent when clickwrap is acknowledged
  const handleNextWithClickWrap = () => {
    if (showClickWrap && visitedNodeIds.length === 1) {
      onClickWrapShown?.();
    }
    handleNext();
  };

  const renderCurrentNode = () => {
    if (!currentNode) return null;

    // Handle informational nodes
    if (currentNode.nodeType === 'information') {
      const InformationalComponent = questionTypeRegistry.get('informational')?.component;
      return InformationalComponent ? (
        <InformationalComponent node={currentNode} ref={questionRef} />
      ) : null;
    }

    // Handle question nodes
    const questionComponent = questionTypeRegistry.get(currentNode.nodeSubtype || '');
    if (questionComponent) {
      const QuestionComponent = questionComponent.component;
      return <QuestionComponent node={currentNode} ref={questionRef} key={currentNode.id} />;
    }

    // Fallback for unknown question types
    // Report to Sentry so we know about misconfigured forms
    Sentry.captureMessage('Unknown question type in FormWizard', {
      level: 'warning',
      tags: {
        unknown_question_type: true,
      },
      extra: {
        nodeSubtype: currentNode.nodeSubtype,
        nodeId: currentNode.id,
        nodeType: currentNode.nodeType,
      },
    });

    return (
      <div ref={questionRef}>
        <p>Unknown question type: {currentNode.nodeSubtype}</p>
      </div>
    );
  };

  if (responseState.isComplete) {
    return (
      <div className="w-3/4 mx-auto">
        <h3 className="text-3xl font-bold p-6">
          {t('form.thanks')}
        </h3>
        <p className="text-gray-500 text-xl font-semibold">
          {t('form.submissionConfirmation')}
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 mx-auto text-left">
      {renderCurrentNode()}
      
      <HStack className='mt-4 py-4'>
        {canGoBack && (
          <Button
            variant="outline"
            className='bg-primary/20 min-w-24 sm:min-w-20'
            onClick={goToPreviousNode}
          >
            {t('form.buttons.back')}
          </Button>
        )}
        <Button
          className='min-w-24 sm:min-w-20'
          onClick={handleNextWithClickWrap}
          disabled={responseState.isSaving}
        >
          {responseState.isSaving ? t('common.loading') : forwardButtonText()}
        </Button>
      </HStack>
      
      <Separator className="mt-4" />
      {showClickWrap && visitedNodeIds.length === 1 ? (
        <p className="text-center text-sm pt-3">
          {t('booking.footer.confirmNext')}
          <a
            className="text-teal-700 hover:underline"
            target="_blank"
            href={`https://${client?.slug}.myclinically.com/privacy`}
            aria-label={t('aria.terms')}
          >
            {t('booking.footer.terms')}
          </a>
          {" "}{t('booking.footer.and')}{" "}
          <a
            className="text-teal-700 hover:underline"
            target="_blank"
            href={`https://${client?.slug}.myclinically.com/privacy`}
            aria-label={t('aria.privacy')}
          >
            {t('booking.footer.privacy')}
          </a>
        </p>
      ) : (
        <p className="text-center text-sm pt-3">
          {t('form.questionFooter', { clinicName: branding?.name })}
        </p>
      )}
    </div>
  );
}

// Main wizard component - thin wrapper that provides context and renders content
export default function FormWizard({
  form,
  onSave,
  onComplete,
  onBack,
  responseId,
  dunBtnTxt,
  onFirstQuestionChange,
  showClickWrap,
  onClickWrapShown,
}: WizardProps) {
  return (
    <FormWizardProvider
      form={form}
      onSave={onSave}
      onComplete={onComplete}
      onBack={onBack}
      responseId={responseId}
    >
      <WizardContent dunBtnTxt={dunBtnTxt} onFirstQuestionChange={onFirstQuestionChange} showClickWrap={showClickWrap} onClickWrapShown={onClickWrapShown} />
    </FormWizardProvider>
  );
}
